import Logger from '../lib/logger';
import { fetchVariantGalleryDocument } from '../lib/variantGallery';
import event from '../lib/mixins/event';
import type { EventEmitter } from '../lib/mixins/event';

const CART_ADD_ENDPOINT = '/cart/add.js';

/** One entry of the JSON sections/b2b-product.liquid renders into `[data-b2b-variant-data]`. */
interface B2bVariant {
	id: number;
	/** Already money-formatted by Liquid. */
	price: string;
	price_cents: number;
	available: boolean;
	min: number;
	max: number | null;
	increment: number;
}

// The theme's `ThemeEvents.variantUpdate`. Its media gallery listens for this
// on the enclosing `.shopify-section` and swaps itself for the gallery found in
// `event.detail.data.html`, which is how the retail PDP changes the pack shot
// when the size changes. Named here rather than imported because the theme's
// modules aren't part of this bundle.
const VARIANT_UPDATE_EVENT = 'variant:update';

// The theme's `ThemeEvents.quantitySelectorUpdate`, fired on the input by the
// stepper after +/- and on blur. `stepUp()` doesn't raise a native event, so
// this is the only signal for the buttons.
const QUANTITY_UPDATE_EVENT = 'quantity-selector:update';

let cleanFetch: typeof fetch | null = null;

/**
 * unpatchedFetch - A `fetch` that UpCart hasn't wrapped
 *
 * UpCart patches window.fetch, XMLHttpRequest.prototype.send AND
 * HTMLFormElement.prototype.submit, and handles any add to cart it catches in
 * its own way. Adding through a clean fetch and only then telling UpCart (see
 * handleSubmit) keeps the error path and the order of events ours.
 *
 * A same-origin iframe gets its own fresh realm whose globals nothing has
 * touched. Borrowing its `fetch` reaches Shopify's cart API directly. Verified
 * against the live app: no drawer, no interception.
 *
 * The frame is kept, not torn down per call. Removing it destroys the realm the
 * Response belongs to, and a Response whose realm is gone can't have its body
 * read — the add succeeds and then `.json()` rejects.
 */
const unpatchedFetch = (input: string, init: RequestInit) => {
	if (!cleanFetch) {
		const frame = document.createElement('iframe');
		frame.style.display = 'none';
		document.body.appendChild(frame);
		// Non-null once the frame is attached to the document.
		const frameWindow = frame.contentWindow as Window;
		cleanFetch = frameWindow.fetch.bind(frameWindow);
	}

	return cleanFetch(input, init);
};

/** Object.assign below grafts the event mixin onto the prototype at runtime. */
interface B2bOrderForm extends EventEmitter {}

class B2bOrderForm {

	// Vars
	// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

	NAME: string;
	dom: HTMLFormElement;
	/** A <select> with several variants, a hidden <input> with one. */
	variantSelect: HTMLSelectElement | HTMLInputElement | null;
	quantityInput: HTMLInputElement | null;
	addButton: HTMLButtonElement | null;
	lineTotalEl: HTMLElement | null;
	errorEl: HTMLElement | null;
	priceEl: HTMLElement | null;
	variants: B2bVariant[];
	variantChangeHandler?: () => void;
	quantityChangeHandler?: () => void;
	submitHandler?: (e: SubmitEvent) => void;
	galleryAbort?: AbortController;

	/**
	 * Class constructor
	 */
	constructor(dom: HTMLFormElement) {

		this.NAME = 'B2bOrderForm';

		this.dom = dom;
		this.variantSelect = this.dom.querySelector<HTMLSelectElement | HTMLInputElement>('[data-b2b-variant-select]');
		// The input belongs to the theme's quantity-selector-component, so it's
		// found by name rather than by a data attribute of ours.
		this.quantityInput = this.dom.querySelector<HTMLInputElement>('input[name="quantity"]');
		this.addButton = this.dom.querySelector<HTMLButtonElement>('[data-b2b-add]');
		this.lineTotalEl = this.dom.querySelector<HTMLElement>('[data-b2b-line-total]');
		this.errorEl = this.dom.querySelector<HTMLElement>('[data-b2b-error]');

		// Price lives outside the form, alongside the title.
		this.priceEl = document.querySelector<HTMLElement>('[data-b2b-price]');

		this.variants = this.readVariantData();
	}

	/**
	 * readVariantData - Price and quantity rules per variant, rendered by Liquid
	 */
	readVariantData(): B2bVariant[] {
		const dataEl = document.querySelector('[data-b2b-variant-data]');
		if (!dataEl) return [];

		try {
			return JSON.parse(dataEl.textContent ?? '');
		} catch (e) {
			Logger.info(`${this.NAME} > could not parse variant data`);
			return [];
		}
	}

	/**
	 * init - Wire up the form
	 */
	init() {
		Logger.info(`${this.NAME} > init`);

		// A <select> only exists when the product has more than one variant; with
		// one variant it's a hidden input and there is nothing to react to.
		if (this.variantSelect && this.variantSelect.tagName === 'SELECT') {
			this.variantChangeHandler = () => this.applyVariant();
			this.variantSelect.addEventListener('change', this.variantChangeHandler);
		}

		// `input` covers typing; the theme's event covers the +/- buttons.
		this.quantityChangeHandler = () => this.applyLineTotal();
		this.quantityInput?.addEventListener('input', this.quantityChangeHandler);
		this.quantityInput?.addEventListener(QUANTITY_UPDATE_EVENT, this.quantityChangeHandler);

		// Capture phase, and stopImmediatePropagation in the handler: UpCart binds
		// its own submit listener further up, and it must never see this event.
		this.submitHandler = (e) => this.handleSubmit(e);
		this.dom.addEventListener('submit', this.submitHandler, { capture: true });
	}

	/**
	 * currentVariant - The variant record for whatever is selected right now
	 */
	currentVariant() {
		if (!this.variantSelect) return null;
		const id = Number(this.variantSelect.value);
		return this.variants.find(variant => variant.id === id) || null;
	}

	/**
	 * applyVariant - Reflect the selected variant in the price and quantity rules
	 */
	applyVariant() {
		const variant = this.currentVariant();
		if (!variant) return;

		if (this.priceEl) this.priceEl.textContent = variant.price;

		if (this.quantityInput) {
			this.quantityInput.min = String(variant.min);
			this.quantityInput.step = String(variant.increment);

			if (variant.max === null) {
				this.quantityInput.removeAttribute('max');
			} else {
				this.quantityInput.max = String(variant.max);
			}

			// B2B catalogs can set a different minimum per variant, so a quantity
			// that was valid a moment ago may now be below the floor.
			const quantity = Number(this.quantityInput.value);
			if (!quantity || quantity < variant.min) this.quantityInput.value = String(variant.min);
		}

		if (this.addButton) this.addButton.disabled = !variant.available;

		this.applyLineTotal();
		this.refreshGallery(variant.id);
	}

	/**
	 * applyLineTotal - Price × quantity, shown in the add button
	 *
	 * Formatted with Intl against the storefront's active currency rather than
	 * Shopify's money format string, which isn't exposed to this bundle. For a
	 * USD storefront the two agree.
	 */
	applyLineTotal() {
		const variant = this.currentVariant();
		if (!variant || !this.lineTotalEl || !this.quantityInput) return;

		const quantity = Number(this.quantityInput.value) || 0;
		const currency = window.Shopify?.currency?.active || 'USD';
		const locale = document.documentElement.lang || 'en';

		this.lineTotalEl.textContent = new Intl.NumberFormat(locale, { style: 'currency', currency })
			.format((variant.price_cents * quantity) / 100);
	}

	/**
	 * refreshGallery - Re-render this section for the variant and let the theme's
	 * media gallery swap itself for the one in the response
	 *
	 * Same mechanism as the retail variant picker: the section rendering API
	 * gives back this section with the variant's featured media first, and the
	 * gallery component does the replacement. Plain `fetch` is fine here; UpCart
	 * only reacts to cart routes.
	 */
	refreshGallery(variantId: number) {
		const sectionId = this.dom.dataset.sectionId;
		const section = this.dom.closest('.shopify-section');
		if (!sectionId || !section) return;

		this.galleryAbort?.abort();
		this.galleryAbort = new AbortController();

		fetchVariantGalleryDocument({
			pageUrl: window.location.pathname,
			sectionId,
			variantId,
			signal: this.galleryAbort.signal,
		})
			.then(html => {
				section.dispatchEvent(new CustomEvent(VARIANT_UPDATE_EVENT, {
					bubbles: true,
					detail: { resource: null, sourceId: this.variantSelect?.id, data: { html } },
				}));
			})
			.catch(error => {
				if (error.name !== 'AbortError') Logger.info(`${this.NAME} > gallery refresh failed`);
			});
	}

	/**
	 * handleSubmit - Add the line and open the cart drawer
	 *
	 * The drawer keeps the buyer on the page, because a wholesale order usually
	 * holds several products. Its subscribe upsell fails on a B2B cart, so the
	 * drawer only opens once assets/upcart-b2b.js has hidden it and UpCart has
	 * finished loading. Before then, the cart page.
	 */
	handleSubmit(e: SubmitEvent) {
		e.preventDefault();
		e.stopImmediatePropagation();
		Logger.info(`${this.NAME} > submit`);

		if (!this.variantSelect || !this.quantityInput) return;

		this.setError(null);
		this.setBusy(true);

		unpatchedFetch(CART_ADD_ENDPOINT, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/json',
			},
			body: JSON.stringify({
				id: Number(this.variantSelect.value),
				quantity: Number(this.quantityInput.value),
			}),
		})
			.then(response => response.json().then((body: CartAddResponse) => ({ ok: response.ok, body })))
			.then(({ ok, body }) => {
				if (!ok) {
					this.setBusy(false);
					const error = body as CartErrorBody;
					this.setError(error.description || error.message || 'We could not add that to your order.');
					return;
				}

				if (!window.fodzymeB2bDrawerReady || !window.upcartRegisterAddToCart) {
					window.location.href = '/cart';
					return;
				}

				this.setBusy(false);
				window.upcartRegisterAddToCart();
			})
			.catch(() => {
				this.setBusy(false);
				this.setError('Something went wrong. Please try again.');
			});
	}

	/**
	 * setBusy - Lock the button while the add is in flight
	 */
	setBusy(isBusy: boolean) {
		if (!this.addButton) return;
		this.addButton.disabled = isBusy;
		this.addButton.setAttribute('aria-busy', isBusy ? 'true' : 'false');
	}

	/**
	 * setError - Show or clear the inline error
	 */
	setError(message: string | null) {
		if (!this.errorEl) return;

		if (!message) {
			this.errorEl.hidden = true;
			this.errorEl.textContent = '';
			return;
		}

		this.errorEl.textContent = message;
		this.errorEl.hidden = false;
	}

	/**
	 * destroy - Clean up event listeners
	 */
	destroy() {
		Logger.info(`${this.NAME} > destroy`);

		this.galleryAbort?.abort();

		if (this.quantityInput && this.quantityChangeHandler) {
			this.quantityInput.removeEventListener('input', this.quantityChangeHandler);
			this.quantityInput.removeEventListener(QUANTITY_UPDATE_EVENT, this.quantityChangeHandler);
		}

		if (this.variantSelect && this.variantChangeHandler) {
			this.variantSelect.removeEventListener('change', this.variantChangeHandler);
		}

		if (this.submitHandler) {
			this.dom.removeEventListener('submit', this.submitHandler, { capture: true });
		}
	}

}

Object.assign(B2bOrderForm.prototype, event);

export default B2bOrderForm;
